import React from 'react';
import { Box, Grid, ColorModeScript } from '@chakra-ui/react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Outlet, useRoutes, RouteObject } from 'react-router-dom';
import PricingNavigationMenu from './components/pricing/PricingNavigationMenu';
import GlobalServices from './lib/services/GlobalServices';
import PricingTool from './views/pricing/PricingTool';
import QuoteList from './views/quotes/QuoteList';
import QuoteDetail from './views/quotes/QuoteDetail';
import QuoteListV2 from './views/quotes/QuoteListV2';
import QuoteDetailV2 from './views/quotes/QuoteDetailV2';

interface ProtectedRouteProps {
  component: React.ComponentType;
}

function ProtectedRoute({ component }: ProtectedRouteProps) {
  const Component = withAuthenticationRequired(component);

  return <Component />;
}

function NonProductionWarningHeader() {
  return (
    <Box bg="yellow.200" p={2} textAlign="center">
      <strong>Warning:</strong> You are in a non-production environment. Data
      may be reset at any time. Please visit{' '}
      <span style={{ textDecoration: 'underline' }}>
        <a href="https://pricing.cr-polls.com">here</a>
      </span>{' '}
      for production.
    </Box>
  );
}

function PricingHome() {
  const currentHostname = window.location.hostname;
  const isProduction = currentHostname === 'pricing.cr-polls.com';

  return (
    <Box textAlign="center" fontSize="xl">
      {!isProduction && <NonProductionWarningHeader />}
      <PricingNavigationMenu>
        <Grid minH="100vh" p={4}>
          <GlobalServices>
            <Outlet />
          </GlobalServices>
        </Grid>
      </PricingNavigationMenu>
    </Box>
  );
}

function App() {
  const routes: RouteObject[] = [
    {
      path: '/',
      element: <ProtectedRoute component={PricingHome} />,
      children: [
        { index: true, element: <PricingTool /> },
        {
          path: 'pricing',
          element: <QuoteList />, // Default to old quotes for backward compatibility
        },
        {
          path: 'pricing/quotes/old',
          element: <QuoteList />,
        },
        {
          path: 'pricing/quotes/new',
          element: <QuoteListV2 />,
        },
        {
          path: 'pricing/quote',
          element: <PricingTool />,
        },
        {
          path: 'pricing/quote/old/:quoteId',
          element: <QuoteDetail />,
        },
        {
          path: 'pricing/quote/v2/:quoteId',
          element: <QuoteDetailV2 />,
        },
        {
          path: 'pricing/quote/:quoteId', // Legacy route for backward compatibility
          element: <QuoteDetail />,
        },
      ],
    },
    {
      path: '*',
      element: <p>UI Router path not found</p>,
    },
  ];

  const routingElement = useRoutes(routes);
  return (
    <Box>
      {routingElement}
      <ColorModeScript />
    </Box>
  );
}

export default App;
